import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
    FilesetResolver,
    FaceLandmarker,
    NormalizedLandmark,
} from "@mediapipe/tasks-vision";
import { useTranslation } from "react-i18next";

type Props = {
    imageSrc: string;
    loadingText: string;
};

const FACE_CONTOUR_INDEXES = [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
    361, 288, 397, 365, 379, 378, 400, 377, 152,
    148, 176, 149, 150, 136, 172, 58, 132, 93, 234,
    127, 162, 21, 54, 103, 67, 109
];

const FaceScanner = ({ imageSrc, loadingText }: Props) => {
    const [landmarks, setLandmarks] = useState<NormalizedLandmark[] | null>(null);
    const [targetRadius, setTargetRadius] = useState(0);
    const [circleCenter, setCircleCenter] = useState<{ x: number; y: number } | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const detectFace = async () => {
            const vision = await FilesetResolver.forVisionTasks(
                "https://a.tangent.ai/quiz-inline/mediapipe-models/wasm"
            );

            const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
                baseOptions: {
                    modelAssetPath:
                        "https://a.tangent.ai/quiz-inline/mediapipe-models/face_landmarker/float16/1/face_landmarker.task",
                    delegate: "GPU",
                },
                runningMode: "IMAGE",
                numFaces: 1,
            });

            const image = imageRef.current;
            if (!image) return;

            const faceResult = faceLandmarker.detect(image);
            if (faceResult?.faceLandmarks?.length > 0) {
                setLandmarks(faceResult.faceLandmarks[0]);
            }
        };

        const img = imageRef.current;
        if (img?.complete) {
            detectFace();
        } else if (img) {
            img.onload = detectFace;
        }
    }, [imageSrc]);

    useEffect(() => {
        if (!landmarks || !imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();

        const xs = FACE_CONTOUR_INDEXES.map(i => landmarks[i].x * rect.width);
        const ys = FACE_CONTOUR_INDEXES.map(i => landmarks[i].y * rect.height);

        const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
        const faceHeight = Math.max(...ys) - Math.min(...ys);

        setCircleCenter({ x: centerX, y: centerY });
        setTargetRadius((faceHeight / 2) * 1.1);
    }, [landmarks]);

    return (
        <div className="tngnt-flex tngnt-flex-col tngnt-items-center tngnt-mt-[64px]">
            <div className="tngnt-relative">
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="Face"
                    className="tngnt-w-[350px] tngnt-h-auto"
                />

                {landmarks && circleCenter && (
                    <svg
                        className="tngnt-absolute tngnt-top-0 tngnt-left-0 tngnt-w-full tngnt-h-full tngnt-pointer-events-none"
                    >
                        {/* Outer solid animated circle */}
                        <motion.circle
                            cx={circleCenter.x}
                            cy={circleCenter.y}
                            initial={{ r: 0 }}
                            animate={{ r: targetRadius }}
                            transition={{ duration: 0.5 }}
                            stroke="rgba(173, 216, 230, 0.3)"
                            strokeWidth={4}
                            fill="none"
                        />

                        {/* Inner dashed rotating circle */}
                        <motion.g
                            className="tngnt-absolute tngnt-top-0 tngnt-left-0"
                            animate={{ rotate: 360 }}
                            transition={{
                                repeat: Infinity,
                                ease: "linear",
                                duration: 14
                            }}>
                            <motion.circle
                                cx={circleCenter.x}
                                cy={circleCenter.y}
                                initial={{ r: 0 }}
                                animate={{ r: Math.max(targetRadius - 8, 0) }}
                                transition={{ duration: 0.7 }}
                                stroke="rgba(173, 216, 230, 0.5)"
                                strokeWidth={3}
                                strokeDasharray="26 12"
                                fill="none"
                            />
                        </motion.g>
                        <motion.g
                            className="tngnt-absolute tngnt-top-0 tngnt-left-0"
                            animate={{ rotate: -360 }}
                            transition={{
                                repeat: Infinity,
                                ease: "linear",
                                duration: 14
                            }}>
                            <motion.circle
                                cx={circleCenter.x}
                                cy={circleCenter.y}
                                initial={{ r: 0 }}
                                animate={{ r: Math.max(targetRadius - 16, 0) }}
                                transition={{ duration: 0.9 }}
                                stroke="rgba(173, 216, 230, 0.6)"
                                strokeWidth={3}
                                strokeDasharray="12 10"
                                fill="none"
                            />
                        </motion.g>
                    </svg>
                )}
            </div>

            <div className="tngnt-flex tngnt-justify-center tngnt-items-center tngnt-m-[20px]">
                <span className="tangent_global_style tngnt-border-solid tngnt-text-[15px]">
                    {loadingText ? loadingText : t("selfieUploadLoading")}
                </span>
            </div>
        </div>
    );
};

export default FaceScanner; 